/* eslint-disable max-lines */
import {Lang} from 'yougile-lang'
import {Util} from 'yougile-util'
import {
  DataObjectId,
  EntitledHubLike,
  Hub,
  HubLike,
  noBoardContainerId,
  SandboxTree,
  Tag
} from 'yougile-model'
import {Items} from '@api/model/items'
import {DepartmentsClient} from '@client/departments-client'
import {PermissionsChecker} from '@client/permissions-checker'
import {Sync} from '@client/sync'
import {SyncCommitHandler} from '@client/sync-commit-handler'
import {Backend} from '@common/backend'
import {Tracking, TrackingEvent} from '@common/tracking'
import {MessageBox} from '@components/message-box'
import {Gantt} from '@extensions/gantt'
import {GanttDraft} from '@extensions/gantt-draft'
import {Notifier} from '../api/model/notifier'
import {renameAllowed} from '../client/hub-actions-allowed'
import {Board} from './board'
import {updateWhenTaskMovedToCol} from './board-settings/update-when-task-moved-to-col'
import {Feedback} from './feedback'
import {globals} from './globals-base'
import {idTasksApi} from './id-tasks/id-tasks-store'
import {isMirroredHub} from './mirrored-tasks/is-mirrored-hub'
import {Order} from './order'
import {PageService} from './page-service'
import {BoardFilters} from './sticker/board-filters'
import {SubtaskUtil} from './subtask-util'
import {Task} from './task'

const mirrorsCache: Map<DataObjectId, boolean> = new Map()

export type ColumnHub = Hub & {isMirrored?: boolean}

export class Column {
  /**
   * board id -> order
   */
  private static tasksOrder: Map<DataObjectId, Order> = new Map()

  static clean(): void {
    for (const order of Column.tasksOrder.values()) {
      order.cleanup()
    }
    Column.tasksOrder.clear()
  }

  static invalidateTasksOrderCache(columnId: DataObjectId): void {
    const order = Column.tasksOrder.get(columnId)
    if (order) {
      order.cleanup()
      Column.tasksOrder.delete(columnId)
    }
  }

  /**
   * This method is called very often and it impacts on overall performance,
   * so the caching is used.
   */
  static isMirror(column: HubLike): boolean {
    if (!column) {
      return false
    }
    if (mirrorsCache.has(column.id)) {
      return mirrorsCache.get(column.id) ?? false
    }
    const res = Sync.get(column).hasChildWithTitle(column.id, '.mirror', 'Tag')
    mirrorsCache.set(column.id, res)
    return res
  }

  static getMirroredColumns(column: HubLike): Hub[] {
    if (!Column.isMirror(column)) {
      throw new Error('The column is not a mirror')
    }
    const box = Sync.get(column)
    const mirrorTagId = box.findIdByTitle('Tag', '.mirror', column.id)
    if (!mirrorTagId) {
      return []
    }
    return box
      .idsToHubs(box.forwardConnections(mirrorTagId))
      .filter(h => !!h && !box.isDeleted(h.id) && !Column.isMirror(h))
  }

  static setMirror(column: HubLike, columns: HubLike[], force = false): void {
    if (!column) {
      return
    }
    const box = Sync.get(column)
    if (!force && !columns?.length) {
      if (Column.isMirror(column)) {
        const tag = box.findTagByTitle('.mirror', column.id, false)
        if (tag) {
          box.removeConnections(tag.id)
          box.remove(tag)
        }
      }
      mirrorsCache.set(column.id, false)
    } else {
      if (!Column.isMirror(column)) {
        box.add(new Tag(column.companyId, '.mirror'), column.id)
      }
      const tag = box.findTagByTitle('.mirror', column.id, false)
      box.removeConnections(tag.id)
      if (columns) {
        columns.forEach((g: Hub) => {
          box.connect(tag.id, g.id)
        })
      }
      mirrorsCache.set(column.id, true)
    }
  }

  static getTasksOrder(column: HubLike): Order {
    if (Column.isMirror(column)) {
      throw new Error('Unsupported')
    }
    let order = Column.tasksOrder.get(column.id)
    if (!order) {
      order = new Order(column.companyId)
      order.load(
        column.id,
        column.id,
        Sync.get(column).getChildrenHubs(column.id)
      )
      Column.tasksOrder.set(column.id, order)
    }
    return order
  }

  // eslint-disable-next-line max-lines-per-function
  static async duplicate(
    column: Hub,
    toBoard?: Hub,
    oldNewMap?: Map<DataObjectId, DataObjectId>
  ): Promise<void> {
    if (Column.isMirror(column)) {
      const box = Sync.get(column)
      const board = toBoard || (box.get(column.location) as Hub)
      const title = box.notConflictingTitle(column.title, 'Hub', board.id)
      const dup = Board.addColumn(title, board, true)
      if (!dup) {
        return
      }
      oldNewMap?.set(column.id, dup.id)
      if (column.data) {
        dup.data = Util.clone(column.data)
      }
      const mirrored = Column.getMirroredColumns(column)
      Column.setMirror(dup, mirrored, true)
    } else {
      const localOldNewMap = new Map<DataObjectId, DataObjectId>()
      const tasks = Column.listTasks(column, false)
      if (tasks.length > 100) {
        Backend.report(
          'Large column copied',
          globals.getCompanyId(),
          column.title,
          tasks.length,
          globals.currentUserId
        )
      }
      if (tasks.length > 1000 && !globals.standalone) {
        MessageBox.warn(Lang.msg.largeColumn)
        return
      }
      const box = Sync.get(column)
      const board = toBoard || (box.get(column.location) as Hub)
      const title = box.notConflictingTitle(column.title, 'Hub', board.id)
      const dup = Board.addColumn(title, board, true)
      if (!dup) {
        return
      }
      localOldNewMap.set(column.id, dup.id)
      if (column.data) {
        dup.data = Util.clone(column.data)
      }
      if (column.properties) {
        dup.properties = Util.clone(column.properties)
      }
      dup.description = column.description
      if (column.stickers) {
        dup.stickers = Util.clone(column.stickers)
      }
      if (!toBoard) {
        const ord = Board.getColumnsOrder(board)
        const ix = ord.order(column) + 1
        ord.handleDrop(ix, dup)
      }

      dup.commit()
      Column.sortTasks(column, tasks)
      const archived: Hub[] = []
      const notArchived: Hub[] = []

      tasks.forEach(t => {
        if (t.complete) {
          archived.push(t)
        } else {
          notArchived.push(t)
        }
      })

      const items = notArchived.reverse()
      const archItems = archived.sort(
        (a, b) => (a.complete ?? 0) - (b.complete ?? 0)
      )

      SyncCommitHandler.doInBatch(() => {
        for (const t of items) {
          Task.duplicate(t, dup, undefined, localOldNewMap)
        }
        for (const t of archItems) {
          Task.duplicate(t, dup, undefined, localOldNewMap)
        }
      })

      if (oldNewMap) {
        for (const [oldId, newId] of localOldNewMap) {
          oldNewMap.set(oldId, newId)
        }
      } else {
        SyncCommitHandler.doInBatch(() => {
          for (const newId of localOldNewMap.values()) {
            Gantt.relinkTaskDependencies(newId, localOldNewMap)
          }
        })
      }
    }
  }

  static removeFromBoard(task: Hub): void {
    if (task.location === noBoardContainerId) {
      return
    }
    const sb = Sync.get(task)
    const colWas = sb.get(task.location) as Hub
    sb.moveTreeNode(task, task.location, noBoardContainerId, false, false)
    const ordWas = Column.getTasksOrder(colWas)
    ordWas.checkAndFixEntities()
  }

  static taskDropHandler(
    placeholderIndex: number,
    task: Hub,
    toCol: Hub,
    opts?: {
      ignoreBoardFilters?: boolean
      type?: 'drag' | 'menu' | 'automatic' | 'none'
    }
  ): void {
    if (task.companyId !== toCol.companyId) {
      throw new Error('Moving tasks between companies is disallowed by now')
    }

    const sb = Sync.get(toCol)
    const colWas = sb.get(task.location) as Hub
    const colWasId = task.location
    const isMirror = isMirroredHub(task)

    let newTitle = task.title
    if (task.location !== toCol.id) {
      if (!isMirror) {
        newTitle = sb.notConflictingTitle(task.title, task.dataType, toCol.id)
      }

      if (newTitle !== task.title) {
        const originalTaskLocation = task.location
        task.location = toCol.id
        const renameAllowedInNewColumn = renameAllowed(task.id)
        task.location = originalTaskLocation
        if (!renameAllowedInNewColumn) {
          // if the move is automatic, we don't want to show a notification because of move is done on server side anyway
          if (opts?.type !== 'automatic') {
            Notifier.warn(
              Lang.msg.noPermissionToRenameInTargetColumnMoveCancelled
            )
          }
          return
        }
      }
    }

    if (
      colWas &&
      colWas.id !== noBoardContainerId &&
      toCol &&
      colWas.id !== toCol.id
    ) {
      const canMoveByPermissions = PermissionsChecker.checkMove(
        task,
        colWas,
        toCol
      )
      const canMoveByItemsRegistry = canMoveByPermissions
        ? Items.canMove(
            Items.fromHub(task),
            Items.fromHub(colWas),
            Items.fromHub(toCol)
          )
        : {success: false}

      if (!canMoveByPermissions || !canMoveByItemsRegistry.success) {
        if (!canMoveByPermissions) {
          MessageBox.warn(Lang.msg.permissionsForbiddenMove)
        }
        const avoidMoveTreeNode = Boolean(
          canMoveByItemsRegistry.avoidMoveTreeNode
        )
        if (!avoidMoveTreeNode) {
          sb.moveTreeNode(task, task.location, toCol.id, true, false)
          setTimeout(() => {
            sb.moveTreeNode(task, task.location, colWas.id, true, false)
          }, 1000)
        }
        return
      }

      if (
        Feedback.isFeedbackDoneColumn(toCol) &&
        !Feedback.isFeedbackDoneColumn(colWas)
      ) {
        Feedback.closeTicket(task)
      } else if (
        !Feedback.isFeedbackDoneColumn(toCol) &&
        Feedback.isFeedbackDoneColumn(colWas)
      ) {
        Feedback.openTicket(task)
      }
    }
    const ord = Column.getTasksOrder(toCol)
    ord.checkAndFixEntities()

    if (colWas && colWas.id !== noBoardContainerId) {
      const ordWas = Column.getTasksOrder(colWas)
      ordWas.checkAndFixEntities()
    }

    const filtered: Hub[] = Column.listTasks(toCol)
      .filter(t => !Task.isArchived(t))
      .sort((a, b) => ord.order(a) - ord.order(b))

    if (filtered.length === 0) {
      ord.handleDrop(0, task)
    } else if (placeholderIndex === 0) {
      let ix = ord.order(filtered[0])
      ix = ix >= 0 ? ix : 0
      ord.handleDrop(ix, task)
    } else {
      const filteredWithoutSelf = filtered.filter(t => t.id !== task.id)
      const afterTask = filteredWithoutSelf[placeholderIndex - 1]
      const afterIx = ord.order(afterTask)
      const taskIx = ord.order(task)
      ord.handleDrop(
        taskIx >= 0 && taskIx < afterIx ? afterIx : afterIx + 1,
        task
      )
    }

    if (task.location === noBoardContainerId && toCol.id) {
      if (opts?.type === 'drag') {
        Tracking.send(TrackingEvent.subtasksMovingSubtaskToTheBoardViaDrag)
      } else if (opts?.type === 'menu') {
        Tracking.send(TrackingEvent.subtasksMovingSubtaskToTheBoardViaMenu)
      } else if (opts?.type === 'none') {
        //
      } else {
        Tracking.send(TrackingEvent.subtasksMovingSubtaskToTheBoard)
      }
      // notify about moving subtask to column as well as for moving tasks
      Items.move(
        Items.fromHub(task),
        Items.fromHub(colWas),
        Items.fromHub(toCol)
      )
    }

    if (task.location !== toCol.id) {
      task.title = newTitle
      if (task.location !== noBoardContainerId) {
        Column.getTasksOrder(sb.get(task.location) as Hub).removeEntity(task)
      }
      sb.moveTreeNode(task, task.location, toCol.id, !isMirror, false)
    }

    updateWhenTaskMovedToCol(task)

    if (colWasId === noBoardContainerId && toCol.id !== noBoardContainerId) {
      setTimeout(() => {
        idTasksApi.makeRequest(task.id)
      }, 1000)
    }

    if (!opts?.ignoreBoardFilters) {
      const currentBoard = PageService.getCurrentBoard()
      if (currentBoard?.id === toCol.location) {
        BoardFilters.pinFiltersToTask(
          currentBoard,
          task,
          colWas.id === toCol.id,
          'task-drop'
        )
      }
    }
  }

  static listTasks(
    column: HubLike,
    withFilter = true,
    options: {board?: Hub; sorted?: boolean; withoutArchive?: boolean} = {}
  ): Hub[] {
    const defaultOptions = {sorted: false, withoutArchive: false}
    const fullOptions = {...defaultOptions, ...options}
    let res: Hub[] = []
    if (Column.isMirror(column)) {
      Column.getMirroredColumns(column).forEach(
        g => (res = [...res, ...Column.listTasks(g, withFilter)])
      )
    } else {
      res = Sync.get(column).getChildrenHubs(column.id)
    }
    if (withFilter && (PageService.getCurrentBoard() || fullOptions.board)) {
      res = BoardFilters.filter(
        fullOptions.board ? fullOptions.board : PageService.getCurrentBoard(),
        res
      )
    }
    const calculatedHierarchy = DepartmentsClient.getHierarchy(Sync.get(column))
    const filtered = res.filter(
      h =>
        (column.companyId === Sync.profileId ||
          PermissionsChecker.checkShow('ignore-departments', h, {
            calculatedHierarchy
          })) &&
        (!fullOptions.withoutArchive || !Task.isArchived(h)) &&
        (column.id !== noBoardContainerId ||
          (SubtaskUtil.getParentTaskIds(h.id).length === 0 && !h.data.private))
    )
    if (fullOptions.sorted) {
      Column.sortTasks(column, filtered)
    }
    return filtered
  }

  static find(column: HubLike): Hub {
    const box = Sync.get(column)
    return box.get(column.id) as Hub
  }

  static editTitle(column: EntitledHubLike, title: string): void {
    if (column.title !== title) {
      const hub = Column.find(column)
      const canEdit = Items.rename(Items.fromHub(hub), title)
      if (canEdit) {
        Sync.get(column).editTitle(hub, title)
      }
    }
  }

  static removeTask(task: HubLike): void {
    const box = Sync.get(task)
    const hub = box.get(task.id) as Hub
    if (hub) {
      box.remove(hub)
      Column.getTasksOrder(box.get(hub.location) as Hub).cleanDeleted()
      GanttDraft.planEntitySubtree(hub.id, box)
    }
  }

  static sortTasks(col: HubLike, tasks: Hub[]): void {
    if (!col || col.id === noBoardContainerId) {
      return
    }
    const taskOrd = Column.getTasksOrder(col)
    tasks.sort((a, b) => taskOrd.order(a) - taskOrd.order(b))
  }

  static parentBoard(column: HubLike): Hub {
    return Sync.parentHub(column)
  }

  static parentProject(column: HubLike): Hub {
    return Sync.parentHub(Column.parentBoard(column))
  }

  static getById(columnId: DataObjectId): Hub | undefined {
    const box = Sync.getCurrentSandbox()
    return box.getHub(columnId)
  }

  static isAutoTaskCoverManagement(column: Hub): boolean {
    const box = Sync.get(column)
    const board = box.getHub(column?.location)
    if (column && board && board.data.settings?.autoTaskCover?.checked) {
      const columnIds = board.data.settings.autoTaskCover.values ?? []
      if (!columnIds?.length || columnIds.includes(column.id)) {
        return true
      }
    }
    return false
  }
}

export const getColumnByTaskId = (
  box: SandboxTree,
  obj: Hub | undefined
): Hub | undefined => {
  let failCounter = 20
  let parentTaskId: DataObjectId | undefined

  while (failCounter && obj) {
    if (obj.location !== noBoardContainerId) {
      return box.getHub(obj.location)
    }

    parentTaskId = SubtaskUtil.getParentTaskIds(obj.id)[0]
    if (!parentTaskId) {
      return undefined
    }

    obj = box.getHub(parentTaskId)

    failCounter--
  }
  return undefined
}
